import axios, { AxiosError, AxiosResponse } from "axios"
import { useLogger } from "../hooks/LoggerHook"

type BonzaError = {
    errors: {
        name: string[]
    }
    message: string
}

const parseAxiosError = (
    route: string,
    error: AxiosError,
    subject?: string
) => {
    const { error: logError } = useLogger(subject ?? "Axios")
    logError(`Error on route: ${route}`)
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        logError({ message: `Response error`, response: error.response })
        return `Error: ${(error.response.data as BonzaError).message}`
    } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        logError(`Request error: ${error.request}`)
        return "The request timed out, if the issue persists please restart Bonza."
    } else {
        // Something happened in setting up the request that triggered an Error
        logError(`Client error: ${error.message}`)
        return "There was an problem sending your request."
    }
}

const getRequest = <T>(route: string, loggerSubject?: string) => {
    const res = axios
        .get(route)
        .then((response: AxiosResponse<T>) => {
            return response.data
        })
        .catch((error: AxiosError) => {
            throw Error(parseAxiosError(route, error, loggerSubject))
        })

    return res
}

const postRequest = <TData, TResponse>(
    route: string,
    data?: TData,
    loggerSubject?: string
) => {
    const res = axios
        .post(route, data)
        .then((response: AxiosResponse<TResponse>) => {
            return response.data
        })
        .catch((error: AxiosError) => {
            throw Error(parseAxiosError(route, error, loggerSubject))
        })

    return res
}

const putRequest = <TData, TResponse>(
    route: string,
    data: TData,
    loggerSubject?: string
) => {
    const res = axios
        .put(route, data)
        .then((response: AxiosResponse<TResponse>) => {
            return response.data
        })
        .catch((error: AxiosError) => {
            throw Error(parseAxiosError(route, error, loggerSubject))
        })

    return res
}

const patchRequest = <TData, TResponse>(
    route: string,
    data: TData,
    loggerSubject?: string
) => {
    const res = axios
        .patch(route, data)
        .then((response: AxiosResponse<TResponse>) => {
            return response.data
        })
        .catch((error: AxiosError) => {
            throw Error(parseAxiosError(route, error, loggerSubject))
        })

    return res
}

const deleteRequest = <T>(route: string, loggerSubject?: string) => {
    const res = axios
        .delete(route)
        .then((response: AxiosResponse<T>) => {
            return response.data
        })
        .catch((error: AxiosError) => {
            throw Error(parseAxiosError(route, error, loggerSubject))
        })

    return res
}

export { getRequest, postRequest, putRequest, patchRequest, deleteRequest }
